import {
  AgaveGlCode,
  AgaveProject,
  AgaveCostCode,
  AgaveCostType,
  AgaveSubJob,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveEmployeeEntity,
  AgaveSubaccount,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
} from 'API';
import { useState, useCallback, useRef, useEffect, useId, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAgaveSearch, SEARCH_CONFIGS } from './useAgaveSearch';
import { filterSelectableCodes } from 'app/util/agaveCodeStatus';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { useAgaveSourceSystem } from 'app/hooks/useAgaveSourceSystem';
import { collapseBySourceId } from 'app/pages/overview/tabs/transactions/utils';
import { isEmDepartmentScopingEnabled } from 'app/util/emDepartmentScoping';
import {
  SearchType,
  selectAgaveSlice,
  searchGlCodes as searchGlCodesAction,
  searchProjects as searchProjectsAction,
  searchVendors as searchVendorsAction,
  searchLocations as searchLocationsAction,
  searchDepartments as searchDepartmentsAction,
  searchSubaccounts as searchSubaccountsAction,
  searchEquipment as searchEquipmentAction,
  searchEmployees as searchEmployeesAction,
  loadMoreGlCodes as loadMoreGlCodesAction,
  loadMoreProjects as loadMoreProjectsAction,
  loadMoreVendors as loadMoreVendorsAction,
  loadMoreLocations as loadMoreLocationsAction,
  loadMoreDepartments as loadMoreDepartmentsAction,
  loadMoreEmployees as loadMoreEmployeesAction,
  loadMoreSubaccounts as loadMoreSubaccountsAction,
  loadMoreEquipment as loadMoreEquipmentAction,
  clearDropdownInitialization,
} from 'app/pages/store/agaveSlice';

/**
 * Combined filter type for all Agave coding search filters.
 * This is a superset of properties from:
 * - SearchAgaveGLCodesFilterInput
 * - SearchAgaveProjectsFilterInput
 * - SearchAgaveCostCodesFilterInput
 * - SearchAgaveVendorsFilterInput
 * - SearchAgaveCostTypesFilterInput
 * - SearchAgaveSubJobsFilterInput
 * - SearchAgaveLocationsFilterInput
 * - SearchAgaveDepartmentsFilterInput
 */
// ---------------------------------------------------------------------------
// Module-level cache — survives mount/unmount from virtualized rows
// ---------------------------------------------------------------------------

import {
  AgaveCodingCacheEntry as CacheEntry,
  agaveInflightRequests,
  getAgaveCached,
  invalidateAgaveOptionsCache,
  makeAgaveCacheKey,
  setAgaveCached,
} from './agaveCodingDropdownCache';

export { invalidateAgaveOptionsCache };

interface AgaveCodingFilter {
  paidolId?: string | null;
  ids?: Array<string | null> | null;
  searchTerm?: string | null;
  cardGroupId?: string | null;
  isSettingsRequest?: boolean | null;
  projectId?: string | null;
  costCodeId?: string | null;
  subJobId?: string | null;
  locationId?: string | null;
  departmentId?: string | null;
  employeeId?: string | null;
  // SEE-1325: the EM cascade. `emGroup` comes off the selected equipment and is
  // required by both EM resolvers; `emCo`/`department` drive the SEE-1435
  // department scoping; `emCostCodeId` applies the cost-code/cost-type pair.
  emGroup?: string | null;
  emCo?: string | null;
  department?: string | null;
  emCostCodeId?: string | null;
}

interface UseAgaveCodingDropdownOptions {
  codingType: SearchType;
  paidolId: string;
  cardGroupId?: string;
  projectId?: string;
  costCodeId?: string;
  subJobId?: string;
  locationId?: string;
  departmentId?: string;
  employeeId?: string;
  emGroup?: string;
  emCo?: string;
  department?: string;
  emCostCodeId?: string;
  enabled?: boolean;
  /** When true, filter-dependent types (subJobs, costCodes, costTypes, locations, departments, employees) do not auto-fetch on mount/filter change; options are loaded only when loadOptions() is called (e.g. when dropdown opens). Use in table rows to avoid stale shared state. */
  fetchOnlyOnOpen?: boolean;
}

interface UseAgaveCodingDropdownReturn<T> {
  currentItems: T[];
  isSearching: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  searchTerm: string | undefined;
  handleSearch: (term: string) => void;
  handleClearSearch: () => void;
  /** No-op on Agave; exists so the Flex and Agave hooks share the same return shape. */
  handleFocus: () => void;
  handleLoadMore: () => Promise<void>;
  /** Load options with current filters. Call when dropdown opens when fetchOnlyOnOpen is true. */
  loadOptions: () => void;
}

const GLOBAL_STATE_KEYS = {
  glCodes: 'searchGlCodes',
  projects: 'searchProjects',
  costCodes: 'searchCostCodes',
  costTypes: 'searchCostTypes',
  subJobs: 'searchSubJobs',
  vendors: 'searchVendors',
  locations: 'searchLocations',
  departments: 'searchDepartments',
  subaccounts: 'searchSubaccounts',
  equipment: 'searchEquipment',
  emCostCodes: 'searchEmCostCodes',
  emCostTypes: 'searchEmCostTypes',
  employees: 'searchEmployees',
} as const;

const SEARCHING_KEYS = {
  glCodes: 'searchingGlCodes',
  projects: 'searchingProjects',
  costCodes: 'searchingCostCodes',
  costTypes: 'searchingCostTypes',
  subJobs: 'searchingSubJobs',
  vendors: 'searchingVendors',
  locations: 'searchingLocations',
  departments: 'searchingDepartments',
  subaccounts: 'searchingSubaccounts',
  equipment: 'searchingEquipment',
  emCostCodes: 'searchingEmCostCodes',
  emCostTypes: 'searchingEmCostTypes',
  employees: 'searchingEmployees',
} as const;

export const useAgaveCodingDropdown = <
  T extends
    | AgaveGlCode
    | AgaveProject
    | AgaveCostCode
    | AgaveCostType
    | AgaveVendor
    | AgaveSubJob
    | AgaveLocation
    | AgaveDepartment
    | AgaveSubaccount
    | AgaveEquipment
    | AgaveEmCostCode
    | AgaveEmCostType
    | AgaveEmployeeEntity
>({
  codingType,
  paidolId,
  cardGroupId,
  projectId,
  costCodeId,
  subJobId,
  locationId,
  departmentId,
  employeeId,
  emGroup,
  emCo,
  department,
  emCostCodeId,
  enabled = true,
  fetchOnlyOnOpen = false,
}: UseAgaveCodingDropdownOptions): UseAgaveCodingDropdownReturn<T> => {
  const dropdownId = useId();
  const dispatch = useAppDispatch();
  const agaveState = useAppSelector(selectAgaveSlice);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);

  const [isLoadingMoreGlobal, setIsLoadingMoreGlobal] = useState(false);
  const hasInitializedRef = useRef(false);

  // Local state for filter-dependent types (costCodes, costTypes, subJobs, locations, departments, employees).
  // Each dropdown instance gets its own data so that Transaction A's project+subjob filter
  // doesn't overwrite Transaction B's project-only results in shared Redux state.
  const [localItems, setLocalItems] = useState<T[]>([]);
  const [localNextToken, setLocalNextToken] = useState<string | null>(null);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [isLoadingMoreLocal, setIsLoadingMoreLocal] = useState(false);
  const localFetchAbortRef = useRef<AbortController | null>(null);

  // Detect the ERP via useAgaveSourceSystem (paidol.agaveSourceSystemSlug, with
  // agaveAccount as fallback) rather than agaveAccount.source_system directly.
  // Direct ERP-connection (erpSpeedchain) paidols never load agaveAccount, so the
  // old check was always false for them and the project/cost-code/location filters
  // silently dropped. Legacy Agave clients are unchanged (agaveAccount fallback).
  const { isSage300Client, isSageIntacctClient, sourceSystemIn } = useAgaveSourceSystem();
  const isSage300CRE = isSage300Client;
  const isSageIntacct = isSageIntacctClient;
  const isViewpointVista = sourceSystemIn(AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED);
  // SEE-1064 / SEE-952: hide retired (inactive/complete/…) codes from the pickers for
  // the AppXchange ERPs, which ingest every status with no sync-time filter. Spectrum
  // and Vista-AppXchange (erpSpeedchain) opt in; the other ERPs already pull active-only
  // projects at sync time, and legacy Agave-SDK Vista customers (erpSpeedchain = false)
  // keep their original unfiltered behavior.
  const isSpectrum = sourceSystemIn(AgaveSourceSystem.SPECTRUM);
  const isVistaAppXchange = isViewpointVista && !!selectedPaidol?.erpSpeedchain;
  const shouldHideRetiredCodes = isSpectrum || isVistaAppXchange;
  // Both Sage 300 CRE and Sage Intacct support filtering by project/cost code
  const supportsCostTypeFiltering = isSage300CRE || isSageIntacct || isViewpointVista || isSpectrum;
  // SEE-1112: Sage Intacct paidols without the Projects module source cost
  // codes/types from GL dimensions (Class/Item), which have no project
  // relationships — so filtering by project would return nothing. Show all,
  // same rationale as the Vista bypass below.
  const intacctProjectsNotLicensed = !!selectedPaidol?.intacctProjectsNotLicensed;
  const departmentScopingEnabled = isEmDepartmentScopingEnabled(selectedPaidol);
  // Sage Intacct supports filtering locations/departments by project
  const supportsLocationDepartmentFiltering = isSageIntacct;

  const search = useAgaveSearch<T>({
    dropdownId,
    useGlobalState: true,
    searchType: codingType,
    cardGroupId,
    projectId,
    costCodeId,
    subJobId,
    locationId,
    departmentId,
    employeeId,
    emGroup,
    emCo,
    department,
    emCostCodeId,
  });
  const { clearSearch } = search;

  const globalStateKey = GLOBAL_STATE_KEYS[codingType];
  const searchingKey = SEARCHING_KEYS[codingType];
  const globalData = agaveState[globalStateKey];
  const isSearchingGlobal = agaveState[searchingKey];

  // SEE-944 — projects can be narrowed by the transaction's selected location.
  // We DON'T re-check supportsLocationDepartmentFiltering here because the
  // caller already gates the locationId on the Sage-Intacct-specific paidol
  // toggle. Re-checking inside the hook caused a race on mobile where
  // `agaveAccount` hadn't loaded yet, so the hook treated locationId as a
  // no-op and fell back to the Redux global projects list.
  const isProjectsFilteredByLocation = codingType === 'projects' && !!locationId;

  const isFilterDependentType =
    codingType === 'subJobs' ||
    codingType === 'costCodes' ||
    codingType === 'costTypes' ||
    // SEE-1325: both EM lists depend on the selected equipment (emGroup, and the
    // department pair), and cost types additionally on the selected cost code.
    codingType === 'emCostCodes' ||
    codingType === 'emCostTypes' ||
    (supportsLocationDepartmentFiltering &&
      (codingType === 'locations' || codingType === 'departments' || codingType === 'employees')) ||
    isProjectsFilteredByLocation;

  // Build the filter for filter-dependent types (used by both fetchLocal and loadOptions)
  const buildFilterDependentFilter = useCallback((): AgaveCodingFilter | null => {
    if (!selectedPaidol?.id || !isFilterDependentType) return null;

    const baseFilter: AgaveCodingFilter = { paidolId: selectedPaidol.id };

    if (codingType === 'subJobs') {
      if (!projectId) return null;
      baseFilter.projectId = projectId;
      baseFilter.costCodeId = costCodeId || undefined;
    } else if (codingType === 'costCodes') {
      // SEE-1112: dimension-sourced Intacct (no Projects module) has no
      // project->cost-code relationships, so filtering would return nothing — show all.
      if (supportsCostTypeFiltering && !intacctProjectsNotLicensed) {
        if (subJobId) baseFilter.subJobId = subJobId;
        if (projectId) baseFilter.projectId = projectId;
      }
    } else if (codingType === 'costTypes') {
      // SEE-975: DIRECT Vista (AppXchange) now has its CostCodeCostType join
      // populated by the ingest, so scope cost types to the selected cost code.
      // Legacy Agave-SDK Vista (erpSpeedchain = false) keeps the empty join, so
      // it stays excluded and shows the full list rather than blanking.
      // SEE-1112: dimension-sourced Intacct excluded too (no relationships exist).
      if (
        supportsCostTypeFiltering &&
        (!isViewpointVista || isVistaAppXchange) &&
        !intacctProjectsNotLicensed
      ) {
        if (projectId) baseFilter.projectId = projectId;
        if (costCodeId) baseFilter.costCodeId = costCodeId;
      }
    } else if (codingType === 'emCostCodes' || codingType === 'emCostTypes') {
      // Without an EMGroup there is no equipment selected yet; the resolver
      // returns nothing rather than offering codes from other groups.
      if (!emGroup) return null;
      baseFilter.emGroup = emGroup;
      // SEE-1435 department scope. Sent ONLY when the paidol opted in, even
      // though the resolver gates on the setting too: this filter is the cache
      // key (`makeAgaveCacheKey`), so sending emCo/department in both states
      // makes the two produce an identical key. Toggling the setting then
      // served the previous state's cached options until the 5-minute TTL
      // expired or a sync invalidated the cache -- which read as "the toggle
      // does nothing until another sync".
      if (departmentScopingEnabled) {
        if (emCo) baseFilter.emCo = emCo;
        if (department) baseFilter.department = department;
      }
      if (codingType === 'emCostTypes' && emCostCodeId) {
        baseFilter.emCostCodeId = emCostCodeId;
      }
    } else if (codingType === 'locations') {
      // SEE-944 — narrow locations by employeeId when the caller passes one.
      // (Caller gates on the SEE-944 toggle in AgaveDropdownColumnModule.)
      if (employeeId) baseFilter.employeeId = employeeId;
    } else if (codingType === 'departments' || codingType === 'employees') {
      // SEE-808: Sage Intacct department/employee dropdowns show the full
      // list — none narrow another. Project-driven auto-fill handles the
      // convenience case for department (see fetchProjectAutoFillDimensions).
    } else if (codingType === 'projects') {
      // SEE-944 — when locationId is provided (paidol toggle on + location on
      // the transaction), Sage Intacct narrows the project list to projects
      // mapped to that location via AgaveProjectLocation.
      if (locationId) baseFilter.locationId = locationId;
      // cardGroupId is still respected so the hierarchy filter on the Lambda
      // applies on top of the location join.
      if (cardGroupId) baseFilter.cardGroupId = cardGroupId;
    }

    return baseFilter;
  }, [
    selectedPaidol?.id,
    isFilterDependentType,
    codingType,
    projectId,
    costCodeId,
    subJobId,
    locationId,
    employeeId,
    emGroup,
    emCo,
    department,
    emCostCodeId,
    cardGroupId,
    supportsCostTypeFiltering,
    isViewpointVista,
    intacctProjectsNotLicensed,
    isVistaAppXchange,
    departmentScopingEnabled,
  ]);

  // Direct GraphQL fetch for filter-dependent types — writes to local state, not Redux.
  // Uses module-level cache to avoid re-fetching on virtualized row remount.
  const fetchLocal = useCallback(
    async (nextTokenOverride?: string | null) => {
      const filter = buildFilterDependentFilter();
      if (!filter) return;

      const isLoadMore = !!nextTokenOverride;
      const cacheKey = makeAgaveCacheKey(codingType, filter as Record<string, unknown>);

      // Cache check for non-loadMore requests
      if (!isLoadMore) {
        const cached = getAgaveCached(cacheKey);
        if (cached) {
          setLocalItems(cached.items as T[]);
          setLocalNextToken(cached.nextToken);
          setIsLoadingLocal(false);
          return;
        }

        // In-flight dedup
        const inflight = agaveInflightRequests.get(cacheKey);
        if (inflight) {
          setIsLoadingLocal(true);
          const result = await inflight;
          if (result) {
            setLocalItems(result.items as T[]);
            setLocalNextToken(result.nextToken);
          }
          setIsLoadingLocal(false);
          return;
        }
      }

      if (isLoadMore) {
        setIsLoadingMoreLocal(true);
      } else {
        setIsLoadingLocal(true);
      }

      // Cancel any in-flight local fetch
      if (localFetchAbortRef.current) {
        localFetchAbortRef.current.abort();
      }
      const controller = new AbortController();
      localFetchAbortRef.current = controller;

      const fetchPromise = (async (): Promise<CacheEntry | null> => {
        try {
          const config = SEARCH_CONFIGS[codingType];
          const input = {
            input: {
              filter,
              nextToken: nextTokenOverride || null,
              limit: 25,
            },
          };

          const data = (await config.serviceMethod(input)) as {
            items: T[];
            nextToken: string | null;
            total?: number;
          } | null;

          if (controller.signal.aborted) return null;

          if (data) {
            let items: unknown[];
            if (isLoadMore) {
              const existing = getAgaveCached(cacheKey)?.items ?? [];
              items = [...existing, ...(data.items || [])];
            } else {
              items = (data.items || []) as unknown[];
            }

            const entry: CacheEntry = {
              items,
              nextToken: data.nextToken || null,
              total: data.total,
              timestamp: Date.now(),
            };
            setAgaveCached(cacheKey, entry);
            return entry;
          }
          return null;
        } catch {
          return null;
        }
      })();

      if (!isLoadMore) {
        agaveInflightRequests.set(cacheKey, fetchPromise);
      }

      try {
        const entry = await fetchPromise;
        if (controller.signal.aborted) return;

        if (entry) {
          setLocalItems(entry.items as T[]);
          setLocalNextToken(entry.nextToken);
        } else if (!isLoadMore) {
          setLocalItems([]);
        }
      } finally {
        agaveInflightRequests.delete(cacheKey);
        if (!controller.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMoreLocal(false);
          } else {
            setIsLoadingLocal(false);
          }
        }
      }
    },
    [buildFilterDependentFilter, codingType, dispatch]
  );

  // Hydrate from cache on mount so re-appearing rows are instant
  useEffect(() => {
    if (!enabled || !isFilterDependentType) return;
    const filter = buildFilterDependentFilter();
    if (!filter) return;
    const cacheKey = makeAgaveCacheKey(codingType, filter as Record<string, unknown>);
    const cached = getAgaveCached(cacheKey);
    if (cached) {
      setLocalItems(cached.items as T[]);
      setLocalNextToken(cached.nextToken);
    }
  }, [enabled, isFilterDependentType, buildFilterDependentFilter, codingType]);

  const currentItems = useMemo(() => {
    // SubJobs require a project - show empty list if no project selected
    if (codingType === 'subJobs' && !projectId) {
      return [];
    }

    // Pick the source for the current mode.
    let items: T[];
    if (search.searchTerm !== undefined) {
      // For search results, always use the filtered results from useAgaveSearch
      items = search.searchResults;
    } else if (isFilterDependentType) {
      // Filter-dependent types use local state (per-instance) to avoid cross-transaction contamination
      items = localItems;
    } else {
      // Non-filter-dependent types use shared Redux state
      items = (globalData?.items || []) as T[];
    }

    // SEE-1325: the EM lists only ever show rows belonging to the CURRENT
    // equipment's EMGroup, the way the pre-v2Search code filtered in memory.
    // Trusting the fetch isn't enough: fetchLocal only REPLACES localItems on a
    // successful response (failures are swallowed), and the fetchOnlyOnOpen
    // path skips the clearing effect — so on an equipment change the previous
    // group's rows survive until a fetch lands, or forever if it fails, and a
    // user could code a cost code from the wrong EMGroup (Vista rejects that
    // at AP entry). No equipment at all (no emGroup) means no valid options.
    if (codingType === 'emCostCodes' || codingType === 'emCostTypes') {
      if (!emGroup) return [];
      items = items.filter((item) => (item as { emGroup?: string | null }).emGroup === emGroup);
    }

    // Hide retired (inactive/complete/…) codes for the opted-in AppXchange ERPs so
    // cardholders only choose from codes still in use (see shouldHideRetiredCodes).
    // Retired codes stay in the store for historical transactions; we only filter
    // the picker list here.
    const selectableItems = shouldHideRetiredCodes ? filterSelectableCodes(items) : items;

    // SEE-1204: collapse rows sharing a `source_id` (e.g. a QBD automatic -> direct
    // cutover leaves both generations in the same table) to a single representative
    // row. No-op for accounts without a cutover (no shared source_id present).
    const collapsed = collapseBySourceId(selectableItems);

    // SEE-1325: NO client-side ordering here, deliberately. Direct Vista's
    // coding lists are server-ordered in BOTH toggle states (name when off,
    // code when on) and offset-paginated, so pages must be shown VERBATIM —
    // re-sorting them fights the server wherever the JS and backend natural
    // sorts disagree ('116B', '20-001' vs '20-010'), which re-shuffled rows
    // above the viewport on every load-more. Every other ERP's lists were
    // never sorted by this hook, so they stay as their own path produced them.
    return collapsed;
  }, [
    search.searchTerm,
    search.searchResults,
    globalData,
    codingType,
    projectId,
    emGroup,
    isFilterDependentType,
    localItems,
    shouldHideRetiredCodes,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (search.searchTerm !== undefined) {
      await search.loadMore();
      return;
    }

    // Filter-dependent types use local pagination
    if (isFilterDependentType) {
      if (!localNextToken) return;
      await fetchLocal(localNextToken);
      return;
    }

    // Non-filter-dependent types use Redux
    if (!selectedPaidol?.id) return;

    setIsLoadingMoreGlobal(true);
    try {
      const baseFilter: AgaveCodingFilter = { paidolId: selectedPaidol.id };
      if (codingType === 'glCodes' || codingType === 'projects') {
        baseFilter.cardGroupId = cardGroupId || undefined;
      }

      const input = {
        filter: baseFilter,
        nextToken: globalData?.nextToken || null,
        limit: 25,
      };

      switch (codingType) {
        case 'glCodes':
          await dispatch(loadMoreGlCodesAction({ input }));
          break;
        case 'projects':
          await dispatch(loadMoreProjectsAction({ input }));
          break;
        case 'vendors':
          await dispatch(loadMoreVendorsAction({ input }));
          break;
        case 'locations':
          await dispatch(loadMoreLocationsAction({ input }));
          break;
        case 'departments':
          await dispatch(loadMoreDepartmentsAction({ input }));
          break;
        case 'employees':
          await dispatch(loadMoreEmployeesAction({ input }));
          break;
        case 'subaccounts':
          await dispatch(loadMoreSubaccountsAction({ input }));
          break;
        case 'equipment':
          await dispatch(loadMoreEquipmentAction({ input }));
          break;
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoadingMoreGlobal(false);
    }
  }, [
    search,
    selectedPaidol?.id,
    codingType,
    cardGroupId,
    globalData?.nextToken,
    dispatch,
    isFilterDependentType,
    localNextToken,
    fetchLocal,
  ]);

  const hasMore = useMemo(() => {
    // SubJobs require a project - no more items to load if no project selected
    if (codingType === 'subJobs' && !projectId) {
      return false;
    }

    // SEE-1325: the EM twin of the subJobs guard, pairing with currentItems'.
    // localNextToken survives an equipment clear (the clearing effect is
    // skipped for fetchOnlyOnOpen and fetchLocal bails without touching it),
    // so without this the dropdown renders an empty list with a clickable
    // "Load more" that silently does nothing.
    if ((codingType === 'emCostCodes' || codingType === 'emCostTypes') && !emGroup) {
      return false;
    }

    if (search.searchTerm !== undefined) {
      return search.hasMore;
    }

    // SEE-970 — browse (no search term) gates load-more on the presence of a
    // nextToken, exactly like typed search (search.hasMore = !!nextToken). The
    // old `items.length < total` gate compared the *accumulated* page list
    // against a *per-page* `total` sentinel (page size + 1 from the backend), so
    // once the second page was appended items.length overtook total and paging
    // stalled mid-list. Both the local and global paths refresh nextToken every
    // page, so it is the correct signal; `total` is never displayed.
    if (isFilterDependentType) {
      return !!localNextToken;
    }

    return !!globalData?.nextToken;
  }, [
    search.searchTerm,
    search.hasMore,
    globalData,
    codingType,
    projectId,
    emGroup,
    isFilterDependentType,
    localNextToken,
  ]);

  const loadOptions = useCallback(() => {
    if (!isFilterDependentType) return;
    if (codingType === 'subJobs' && !projectId) return;
    // Paired with the subJobs guard above (fetchLocal would bail on the null
    // filter anyway; this keeps the two parent-required guards side by side).
    if ((codingType === 'emCostCodes' || codingType === 'emCostTypes') && !emGroup) return;
    fetchLocal();
  }, [isFilterDependentType, codingType, projectId, emGroup, fetchLocal]);

  // When fetchOnlyOnOpen: trigger load when this row's filters change (e.g. user selected a project)
  // so dependent dropdowns are up to date without clicking out and back in. Skip on initial mount.
  const prevFiltersRef = useRef<{
    projectId: string | undefined;
    subJobId: string | undefined;
    costCodeId: string | undefined;
    locationId: string | undefined;
    employeeId: string | undefined;
    emGroup: string | undefined;
    emCo: string | undefined;
    department: string | undefined;
    emCostCodeId: string | undefined;
  } | null>(null);

  useEffect(() => {
    if (!fetchOnlyOnOpen || !isFilterDependentType || !enabled) return;

    const current = {
      projectId,
      subJobId,
      costCodeId,
      locationId,
      employeeId,
      emGroup,
      emCo,
      department,
      emCostCodeId,
    };

    if (prevFiltersRef.current === null) {
      prevFiltersRef.current = current;
      return;
    }

    const prev = prevFiltersRef.current;
    const changed =
      prev.projectId !== current.projectId ||
      prev.subJobId !== current.subJobId ||
      prev.costCodeId !== current.costCodeId ||
      prev.locationId !== current.locationId ||
      prev.employeeId !== current.employeeId ||
      // SEE-1325: picking different equipment changes emGroup/emCo/department,
      // and picking a cost code changes emCostCodeId — both must refetch, or the
      // EM dropdowns keep showing the previous equipment's options.
      prev.emGroup !== current.emGroup ||
      prev.emCo !== current.emCo ||
      prev.department !== current.department ||
      prev.emCostCodeId !== current.emCostCodeId;

    if (changed) {
      prevFiltersRef.current = current;
      // Clear before refetching, matching the non-fetchOnlyOnOpen effect: the
      // previous context's options must not stay rendered (and selectable)
      // during the in-flight window. The EM cost type list is the sharp case —
      // its pair constraint is server-side only, so unlike emGroup no in-memory
      // filter can catch a stale row, and the auto-clear snackbar actively
      // sends the user back into this dropdown right after a cost code change.
      setLocalItems([]);
      setLocalNextToken(null);
      loadOptions();
    }
  }, [
    fetchOnlyOnOpen,
    isFilterDependentType,
    enabled,
    projectId,
    subJobId,
    costCodeId,
    locationId,
    employeeId,
    emGroup,
    emCo,
    department,
    emCostCodeId,
    loadOptions,
  ]);

  // Initialize search data when component loads (only for non-filter-dependent types).
  // Skip dispatch if Redux already has data for this type (e.g. row scrolled back into view).
  useEffect(() => {
    if (!enabled || !selectedPaidol?.id || hasInitializedRef.current) return;

    // Skip initialization for filter-dependent types - they use the re-fetch logic below
    if (isFilterDependentType) {
      hasInitializedRef.current = true;
      return;
    }

    hasInitializedRef.current = true;

    // Redux already has data for this type — no need to re-dispatch
    if (globalData?.items && globalData.items.length > 0) return;

    const baseFilter: AgaveCodingFilter = { paidolId: selectedPaidol.id };
    if (codingType === 'glCodes' || codingType === 'projects') {
      baseFilter.cardGroupId = cardGroupId || undefined;
    }

    switch (codingType) {
      case 'glCodes':
        dispatch(searchGlCodesAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'projects':
        dispatch(searchProjectsAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'vendors':
        dispatch(searchVendorsAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'locations':
        dispatch(searchLocationsAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'departments':
        dispatch(searchDepartmentsAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'subaccounts':
        dispatch(searchSubaccountsAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'equipment':
        dispatch(searchEquipmentAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
      case 'employees':
        dispatch(searchEmployeesAction({ input: { filter: baseFilter, limit: 25 } }));
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedPaidol?.id, codingType, cardGroupId, dispatch, isFilterDependentType]);

  // Clear search state when dropdown closes
  useEffect(() => {
    if (!enabled) {
      clearSearch();
    }
  }, [enabled, clearSearch]);

  // For filter-dependent types: clear local state and re-fetch when filters change.
  // Each instance fetches directly into its own local state — no Redux contamination.
  // Skip when fetchOnlyOnOpen: options are loaded only when dropdown opens via loadOptions().
  useEffect(() => {
    if (!isFilterDependentType) return;
    if (fetchOnlyOnOpen) return;
    if (!enabled || !selectedPaidol?.id) return;

    // Clear stale local data immediately so the dropdown doesn't flash old results
    setLocalItems([]);
    setLocalNextToken(null);

    // SubJobs require a project
    if (codingType === 'subJobs' && !projectId) return;

    fetchLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    costCodeId,
    subJobId,
    locationId,
    employeeId,
    enabled,
    codingType,
    selectedPaidol?.id,
    isFilterDependentType,
    fetchOnlyOnOpen,
    fetchLocal,
  ]);

  useEffect(() => {
    return () => {
      hasInitializedRef.current = false;
      dispatch(clearDropdownInitialization({ dropdownId }));
      if (localFetchAbortRef.current) {
        localFetchAbortRef.current.abort();
      }
    };
  }, [dispatch, dropdownId]);

  return {
    currentItems,
    isSearching: search.isSearching || isSearchingGlobal || isLoadingLocal,
    isLoadingMore: search.isLoadingMore || isLoadingMoreGlobal || isLoadingMoreLocal,
    hasMore,
    searchTerm: search.searchTerm,
    handleSearch: search.searchAgaveCode,
    handleClearSearch: clearSearch,
    handleFocus: () => undefined,
    handleLoadMore,
    loadOptions,
  };
};
